import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { IFilterItem, IOrderFilter, ITrackedEquipmentFilter } from '../../models';

type OrdersSubPage = 'orders' | 'standingOrders';
type EquipmentSubPage = 'trackedEquipment' | 'equipment' | 'equipmentTypes';
type HelpSubPage = 'patients' | 'orders' | 'equipment' | 'deliverySchedule' | 'reports' | undefined;

type PageState = {
  ordersSubPage: OrdersSubPage;
  equipmentSubPage: EquipmentSubPage;
  helpSubPage: HelpSubPage;
  patientsSelectedKeys: string[];
  patientsFilterValues: string[];
  onDemandOrdersFilterValues: IOrderFilter[];
  onDemandOrderEquipmentTypeFilterValues: IFilterItem[];
  standingOrdersWeekdayValue: string;
  standingOrderEquipmentTypeFilterValues: IFilterItem[];
  trackedEquipmentFilterValue: ITrackedEquipmentFilter;
  deliveryScheduleDate: Date;
  deliveryScheduleFilterValues: string[];

  setOrdersSubPage: (selectedSubPage: string) => void;
  setEquipmentSubPage: (selectedSubPage: string) => void;
  setHelpSubPage: (selectedSubPage: string | undefined) => void;
  setPatientsSelectedKeys: (selectedKeys: string[]) => void;
  setPatientsFilterValues: (filterValues: string[]) => void;
  setOnDemandOrdersFilterValues: (filterValues: IOrderFilter[]) => void;
  setOnDemandOrderEquipmentFilterValues: (FilterValues: IFilterItem[]) => void;
  setStandingOrdersWeekdayValue: (weekdayValue: string) => void;
  setStandingOrderEquipmentFilterValues: (FilterValues: IFilterItem[]) => void;
  setTrackedEquipmentFilterValue: (filterValue: ITrackedEquipmentFilter) => void;
  setDeliveryScheduleDate: (selectedDate: Date) => void;
  setDeliveryScheduleFilterValues: (filterValues: string[]) => void;
};

export const usePageStore = create(
  persist<PageState>(
    (set) => ({
      ordersSubPage: 'orders',
      equipmentSubPage: 'trackedEquipment',
      helpSubPage: undefined,
      patientsSelectedKeys: [],
      patientsFilterValues: ['active'],
      onDemandOrdersFilterValues: [IOrderFilter.Unfulfilled],
      onDemandOrderEquipmentTypeFilterValues: [],
      standingOrdersWeekdayValue: '0',
      standingOrderEquipmentTypeFilterValues: [],
      trackedEquipmentFilterValue: 0,
      deliveryScheduleDate: new Date(),
      deliveryScheduleFilterValues: ['pending', 'urgent', 'delivered', 'undelivered', 'incomplete'],
      applyNestedFilter: false,

      setOrdersSubPage: (selectedSubPage: string) => {
        set(() => ({
          ordersSubPage: selectedSubPage as OrdersSubPage,
        }));
      },
      setEquipmentSubPage: (selectedSubPage: string) => {
        set(() => ({
          equipmentSubPage: selectedSubPage as EquipmentSubPage,
        }));
      },
      setHelpSubPage: (selectedSubPage: string | undefined) => {
        set(() => ({
          helpSubPage: selectedSubPage as HelpSubPage,
        }));
      },
      setPatientsSelectedKeys: (selectedKeys: string[]) => {
        set(() => ({
          patientsSelectedKeys: selectedKeys,
        }));
      },
      setPatientsFilterValues: (filterValues: string[]) => {
        set(() => ({
          patientsFilterValues: filterValues,
        }));
      },
      setOnDemandOrdersFilterValues: (filterValues: IOrderFilter[]) => {
        set(() => ({
          onDemandOrdersFilterValues: filterValues,
        }));
      },
      setOnDemandOrderEquipmentFilterValues: (filterValues: IFilterItem[]) => {
        set(() => ({
          onDemandOrderEquipmentTypeFilterValues: filterValues,
        }));
      },
      setStandingOrdersWeekdayValue: (weekdayValue: string) => {
        set(() => ({
          standingOrdersWeekdayValue: weekdayValue,
        }));
      },
      setStandingOrderEquipmentFilterValues: (filterValues: IFilterItem[]) => {
        set(() => ({
          standingOrderEquipmentTypeFilterValues: filterValues,
        }));
      },
      setTrackedEquipmentFilterValue: (filterValue: ITrackedEquipmentFilter) => {
        set(() => ({
          trackedEquipmentFilterValue: filterValue,
        }));
      },
      setDeliveryScheduleDate: (selectedDate: Date) => {
        set(() => ({
          deliveryScheduleDate: selectedDate,
        }));
      },
      setDeliveryScheduleFilterValues: (filterValues: string[]) => {
        set(() => ({
          deliveryScheduleFilterValues: filterValues,
        }));
      },
    }),
    {
      name: 'odm-page-store',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);
